import { Box, Stack, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import CopyableTypography from "components/Typography/CopyableTypography";
import React from "react";

import { TruncatedText } from "./TruncatedText";

// 14px when using the small prop, 16px by default
const labelFontSize = "0.75rem";
// labels are not rendered when using the small prop
const getValueFontSize = (small: boolean) => (small ? "0.875rem" : "1rem");

export function DisplayOnlyField({
  text,
  subtext,
  label,
  small = false,
  renderValue,
  copyable = true,
  infoTooltip,
}: {
  text?: string;
  subtext?: string | undefined | null;
  label?: React.ReactNode;
  small?: boolean;
  renderValue?: (value: React.ReactNode) => React.ReactNode;
  copyable?: boolean;
  infoTooltip?: React.ReactNode;
}) {
  const valueFontSize = getValueFontSize(small);

  const copyableText = text ? (
    !copyable ? (
      <TruncatedText
        text={text}
        subtext={subtext}
        fontSize={valueFontSize}
        showHoverEffect={false}
      />
    ) : (
      <CopyableTypography
        text={text}
        subtext={subtext}
        sx={{
          fontSize: valueFontSize,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        tooltipOptions={{
          disableHoverMessage: true,
        }}
      />
    )
  ) : (
    <Typography sx={{ fontSize: valueFontSize, minHeight: valueFontSize }} />
  );

  const value = renderValue ? renderValue(copyableText) : copyableText;

  return (
    <Stack spacing={0}>
      {(label || infoTooltip) && (
        <Typography
          variant="body2"
          color="text.secondary"
          fontSize={labelFontSize}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
          }}
        >
          {label}
          {infoTooltip && (
            <InfoIconTooltip
              fontSize="1rem"
              iconButtonSx={{
                ml: "0.25rem",
                padding: "1px",
              }}
              tooltip={infoTooltip}
            />
          )}
        </Typography>
      )}
      <Box title={text}>{value}</Box>
    </Stack>
  );
}

export function DisplayOnlyFieldList({
  items,
  label,
  small = false,
  copyable = true,
}: {
  items: string[];
  label?: string | React.ReactNode;
  small?: boolean;
  copyable?: boolean;
}) {
  const valueFontSize = getValueFontSize(small);
  return (
    <Stack spacing={0}>
      {label && (
        <Typography variant="body2" color="text.secondary" fontSize={labelFontSize}>
          {label}
        </Typography>
      )}
      <Stack>
        {items.map((item, index) =>
          copyable ? (
            <CopyableTypography
              key={index}
              text={item}
              sx={{ fontSize: valueFontSize }}
              tooltipOptions={{
                disableHoverMessage: true,
              }}
            />
          ) : (
            <TruncatedText key={index} text={item} fontSize={valueFontSize} />
          )
        )}
      </Stack>
    </Stack>
  );
}
